import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { fetchGuidesManifest, getGuideOgImageUrl } from '../services/guides';
import type { GuideMeta } from '../types/guide';

const SITE_NAME = 'GenAnything';
const INDEX_TITLE = 'AI Video Guides | How-To & Tips';
const INDEX_DESCRIPTION = 'Expert guides on AI video generation: how-tos, tips, and best practices for creating high-quality AI videos.';

export default function GuidesIndexPage() {
  const [guides, setGuides] = useState<GuideMeta[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGuidesManifest()
      .then((m) => setGuides(m.guides))
      .catch(() => setGuides([]))
      .finally(() => setLoading(false));
  }, []);

  const byCategory = guides.reduce<Record<string, GuideMeta[]>>((acc, g) => {
    const cat = g.category || 'General';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(g);
    return acc;
  }, {});
  const categories = Object.keys(byCategory).sort();

  return (
    <>
      <Helmet>
        <title>{INDEX_TITLE} | {SITE_NAME}</title>
        <meta name="description" content={INDEX_DESCRIPTION} />
        <link rel="canonical" href={typeof window !== 'undefined' ? `${window.location.origin}/guides` : 'https://genanything.ai/guides'} />
        <meta property="og:title" content={INDEX_TITLE} />
        <meta property="og:description" content={INDEX_DESCRIPTION} />
        <meta property="og:url" content={typeof window !== 'undefined' ? `${window.location.origin}/guides` : 'https://genanything.ai/guides'} />
      </Helmet>

      <div className="min-h-screen bg-bg text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
          <nav className="text-sm text-white/60 mb-8" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-white">Guides</span>
          </nav>

          <header className="mb-12">
            <h1 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">
              {INDEX_TITLE}
            </h1>
            <p className="mt-4 text-white/75 text-lg leading-relaxed max-w-2xl">
              {INDEX_DESCRIPTION}
            </p>
          </header>

          {loading ? (
            <div className="text-white/60 animate-pulse">Loading guides...</div>
          ) : guides.length === 0 ? (
            <p className="text-white/60">No guides yet.</p>
          ) : (
            <div className="space-y-12">
              {categories.map((cat) => (
                <section key={cat}>
                  <h2 className="text-lg font-semibold text-white/90 mb-4 capitalize tracking-tight">
                    {cat.replace(/-/g, ' ')}
                  </h2>
                  <ul className="grid gap-4 sm:grid-cols-2">
                    {byCategory[cat].map((g) => (
                      <li key={g.slug}>
                        <Link
                          to={`/guides/${g.slug}`}
                          className="group flex gap-4 rounded-xl border border-white/10 bg-white/5 p-4 transition hover:border-white/20 hover:bg-white/10 hover:shadow-lg"
                        >
                          <div className="h-24 w-32 shrink-0 overflow-hidden rounded-lg bg-white/5 ring-1 ring-white/10">
                            <img
                              src={getGuideOgImageUrl(g)}
                              alt=""
                              className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
                              loading="lazy"
                            />
                          </div>
                          <div className="min-w-0 flex-1">
                            <span className="font-medium text-white group-hover:underline line-clamp-2 block">
                              {g.title}
                            </span>
                            {g.description && (
                              <p className="mt-1.5 line-clamp-2 text-sm text-white/55 leading-snug">
                                {g.description}
                              </p>
                            )}
                          </div>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          )}

          <div className="mt-10 pt-6 border-t border-white/10">
            <Link to="/" className="text-white/70 hover:text-white text-sm">
              ← Home
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
